import { useEffect, useRef, useState } from 'react';
import type {
  AnswerSource,
  AnswerStatus,
  ConnectedAnswers,
  WorldDraft,
} from '../../domain/types.js';
import { WIZARD_QUESTION_IDS } from '../../constants.js';
import { AiFillBlanksButton, SuggestedBadge } from './groundingStep.js';
import {
  applyLocalAiFillForStep,
  blankSummaryForStep,
} from './suggestionProvider.js';
import { OptionalQuestionsToggle } from './optionalQuestions.js';
import {
  CROWD_OPTIONS,
  CONNECTED_QUESTIONS,
  emptyConnectedAnswers,
  firstIncompleteConnectedField,
  patchConnectedCrowd,
  patchConnectedRhythm,
  RHYTHM_OPTIONS,
} from './questionSchema.js';
import './createWorldSteps.css';

function fieldStatus(draft: WorldDraft, questionId: string): AnswerStatus | null {
  return draft.answers[questionId]?.status ?? null;
}

export function ConnectedStep(props: {
  draft: WorldDraft;
  connected: ConnectedAnswers | null;
  onChange: (connected: ConnectedAnswers, options?: { syncOptional?: boolean }) => void;
  onSkipRhythm?: () => void;
  onAiFill?: (next: WorldDraft, summary: string) => void;
  focusFieldId?: string | null;
  optionalOpen?: boolean;
  onOptionalOpenChange?: (open: boolean) => void;
}): JSX.Element {
  const connected = props.connected ?? emptyConnectedAnswers();
  const firstInvalid = props.focusFieldId ?? firstIncompleteConnectedField(props.connected);
  const [optionalOpenLocal, setOptionalOpenLocal] = useState(false);
  const optionalOpen = props.optionalOpen ?? optionalOpenLocal;
  const setOptionalOpen = props.onOptionalOpenChange ?? setOptionalOpenLocal;
  const typeRef = useRef<HTMLTextAreaElement>(null);
  const descRef = useRef<HTMLTextAreaElement>(null);
  const crowdOtherRef = useRef<HTMLInputElement>(null);
  const rhythmOtherRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!props.focusFieldId) return;
    if (
      props.focusFieldId === WIZARD_QUESTION_IDS.connectedCrowd
      || props.focusFieldId === WIZARD_QUESTION_IDS.connectedRhythm
    ) {
      setOptionalOpen(true);
    }
    const map: Record<string, { current: HTMLElement | null }> = {
      [WIZARD_QUESTION_IDS.connectedLocationType]: typeRef,
      [WIZARD_QUESTION_IDS.connectedDescription]: descRef,
      [WIZARD_QUESTION_IDS.connectedCrowd]: crowdOtherRef,
      [WIZARD_QUESTION_IDS.connectedRhythm]: rhythmOtherRef,
    };
    map[props.focusFieldId]?.current?.focus();
  }, [props.focusFieldId, setOptionalOpen]);

  const typeQ = CONNECTED_QUESTIONS[0]!;
  const descQ = CONNECTED_QUESTIONS[1]!;
  const crowdAnswered = fieldStatus(props.draft, WIZARD_QUESTION_IDS.connectedCrowd) !== 'unanswered'
    && fieldStatus(props.draft, WIZARD_QUESTION_IDS.connectedCrowd) !== null;
  const rhythmAnswered = fieldStatus(props.draft, WIZARD_QUESTION_IDS.connectedRhythm) !== 'unanswered'
    && fieldStatus(props.draft, WIZARD_QUESTION_IDS.connectedRhythm) !== null;

  const blankSummary = blankSummaryForStep(props.draft, 'connected');

  function patchRequired(p: Partial<Pick<ConnectedAnswers, 'locationType' | 'description'>>): void {
    props.onChange({ ...connected, ...p }, { syncOptional: false });
  }

  function patchOptional(p: Partial<Pick<ConnectedAnswers, 'crowd' | 'rhythm'>>): void {
    props.onChange({ ...connected, ...p }, { syncOptional: true });
  }

  function handleAiFill(): { summary: string; nothingToFill: boolean; filledLabels: string[] } {
    const outcome = applyLocalAiFillForStep(props.draft, 'connected');
    if (!outcome.nothingToFill) setOptionalOpen(true);
    props.onAiFill?.(outcome.draft, outcome.summary);
    return outcome;
  }

  return (
    <div className="world-form">
      <div
        className={`world-field-row ${firstInvalid === WIZARD_QUESTION_IDS.connectedLocationType ? 'world-field-invalid' : ''}`}
      >
        <div className="world-field-label-row">
          <label htmlFor="connected-type">{typeQ.prompt}</label>
          {fieldStatus(props.draft, WIZARD_QUESTION_IDS.connectedLocationType) === 'suggested' && (
            <SuggestedBadge />
          )}
        </div>
        <textarea
          id="connected-type"
          ref={typeRef}
          rows={3}
          value={connected.locationType}
          onChange={(e) => patchRequired({ locationType: e.target.value })}
          aria-invalid={firstInvalid === WIZARD_QUESTION_IDS.connectedLocationType}
        />
        <p className="world-field-helper">{typeQ.whyItMatters}</p>
      </div>

      <div
        className={`world-field-row ${firstInvalid === WIZARD_QUESTION_IDS.connectedDescription ? 'world-field-invalid' : ''}`}
      >
        <div className="world-field-label-row">
          <label htmlFor="connected-desc">{descQ.prompt}</label>
          {fieldStatus(props.draft, WIZARD_QUESTION_IDS.connectedDescription) === 'suggested' && (
            <SuggestedBadge />
          )}
        </div>
        <textarea
          id="connected-desc"
          ref={descRef}
          rows={4}
          value={connected.description}
          onChange={(e) => patchRequired({ description: e.target.value })}
          aria-invalid={firstInvalid === WIZARD_QUESTION_IDS.connectedDescription}
        />
        <p className="world-field-helper">{descQ.whyItMatters}</p>
      </div>

      <OptionalQuestionsToggle open={optionalOpen} onToggle={() => setOptionalOpen(!optionalOpen)} />
      {optionalOpen && (
        <div className="world-optional-section">
          <fieldset
            className={`world-field-row ${firstInvalid === WIZARD_QUESTION_IDS.connectedCrowd ? 'world-field-invalid' : ''}`}
          >
            <legend className="world-field-label-row">
              Who typically comes here
              {fieldStatus(props.draft, WIZARD_QUESTION_IDS.connectedCrowd) === 'suggested' && (
                <SuggestedBadge />
              )}
            </legend>
            <div className="world-radio-group world-radio-group--connected" role="radiogroup" aria-label="Who typically comes here">
              {CROWD_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`world-radio-option ${crowdAnswered && connected.crowd.type === opt.value ? 'sel' : ''}`}
                >
                  <input
                    type="radio"
                    name="connected-crowd"
                    checked={crowdAnswered && connected.crowd.type === opt.value}
                    onChange={() =>
                      patchOptional({
                        crowd: patchConnectedCrowd(connected.crowd, opt.value),
                      })
                    }
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
            {connected.crowd.type === 'other' && (
              <div className="world-field-row">
                <label htmlFor="crowd-other">Describe who typically comes here</label>
                <input
                  id="crowd-other"
                  ref={crowdOtherRef}
                  value={connected.crowd.otherDescription ?? ''}
                  onChange={(e) =>
                    patchOptional({
                      crowd: { type: 'other', otherDescription: e.target.value },
                    })
                  }
                  aria-invalid={firstInvalid === WIZARD_QUESTION_IDS.connectedCrowd}
                />
              </div>
            )}
          </fieldset>

          <fieldset className="world-field-row">
            <legend className="world-field-label-row">
              Daily rhythm
              {fieldStatus(props.draft, WIZARD_QUESTION_IDS.connectedRhythm) === 'suggested' && (
                <SuggestedBadge />
              )}
            </legend>
            <div className="world-radio-group world-radio-group--connected" role="radiogroup" aria-label="Daily rhythm">
              {RHYTHM_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`world-radio-option ${
                    rhythmAnswered && !connected.rhythm.skipped && connected.rhythm.type === opt.value ? 'sel' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="connected-rhythm"
                    checked={rhythmAnswered && !connected.rhythm.skipped && connected.rhythm.type === opt.value}
                    onChange={() =>
                      patchOptional({
                        rhythm: patchConnectedRhythm(connected.rhythm, opt.value),
                      })
                    }
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
            {connected.rhythm.skipped === false && connected.rhythm.type === 'other' && (
              <div className="world-field-row">
                <label htmlFor="rhythm-other">Describe the daily rhythm</label>
                <input
                  id="rhythm-other"
                  ref={rhythmOtherRef}
                  value={connected.rhythm.otherDescription ?? ''}
                  onChange={(e) =>
                    patchOptional({
                      rhythm: {
                        skipped: false,
                        type: 'other',
                        otherDescription: e.target.value,
                      },
                    })
                  }
                  aria-invalid={firstInvalid === WIZARD_QUESTION_IDS.connectedRhythm}
                />
              </div>
            )}
          </fieldset>
        </div>
      )}

      <AiFillBlanksButton summary={blankSummary} onConfirmFill={handleAiFill} />
    </div>
  );
}

/** Legacy one-question-at-a-time shim — v2 shows all connected fields at once. */
export function ConditionalQuestionStep(props: {
  draft: WorldDraft;
  questionId: string | null;
  onAnswer: (questionId: string, value: unknown, status: AnswerStatus, source: AnswerSource | null) => void;
  onRandomize?: (questionId: string) => void;
  onAssumption?: (questionId: string) => void;
  onSkipOptional?: (questionId: string) => void;
  onConnectedChange?: (connected: ConnectedAnswers, options?: { syncOptional?: boolean }) => void;
  onAiFill?: (next: WorldDraft, summary: string) => void;
  focusFieldId?: string | null;
}): JSX.Element {
  const connected = props.draft.connected ?? emptyConnectedAnswers();

  function emitChange(next: ConnectedAnswers, options?: { syncOptional?: boolean }): void {
    if (props.onConnectedChange) {
      props.onConnectedChange(next, options);
      return;
    }
    const { onAnswer } = props;
    if (next.locationType !== connected.locationType) {
      onAnswer(WIZARD_QUESTION_IDS.connectedLocationType, next.locationType, 'answered', 'user');
    }
    if (next.description !== connected.description) {
      onAnswer(WIZARD_QUESTION_IDS.connectedDescription, next.description, 'answered', 'user');
    }
    if (JSON.stringify(next.crowd) !== JSON.stringify(connected.crowd)) {
      onAnswer(WIZARD_QUESTION_IDS.connectedCrowd, next.crowd, 'answered', 'user');
    }
    if (JSON.stringify(next.rhythm) !== JSON.stringify(connected.rhythm)) {
      const rhythmStatus = next.rhythm.skipped ? 'skipped' as const : 'answered' as const;
      onAnswer(
        WIZARD_QUESTION_IDS.connectedRhythm,
        next.rhythm.skipped ? { skipped: true } : next.rhythm,
        rhythmStatus,
        'user',
      );
    }
  }

  return (
    <ConnectedStep
      draft={props.draft}
      connected={connected}
      focusFieldId={props.focusFieldId}
      onChange={emitChange}
      onSkipRhythm={() => {
        emitChange({ ...connected, rhythm: { skipped: true } }, { syncOptional: true });
        props.onSkipOptional?.(WIZARD_QUESTION_IDS.connectedRhythm);
      }}
      onAiFill={props.onAiFill}
    />
  );
}
